import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import PharmWealthLogo from './PharmWealthLogo';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      setIsAuthenticated(!!user);
      
      // Check if user is admin
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const isUserAdmin = userData.role === 'admin' || userData.role === 'administrator';
            console.log('User role check:', { role: userData.role, isAdmin: isUserAdmin });
            setIsAdmin(isUserAdmin);
          } else {
            console.log('User document does not exist in Firestore');
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/premium-milk', label: 'Premium Milk' },
    { path: '/pharmapp', label: 'PharmApp' },
    { path: '/feed-the-cows', label: 'Feed the Cows' },
    { path: '/rac-program', label: 'RAC Program' },
    { path: '/investors', label: 'Investors' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsMobileMenuOpen(false);
      // Redirect to home page after logout
      if (location.pathname !== '/') {
        window.location.href = '/';
      }
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <motion.nav
      className={`navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <PharmWealthLogo size="compact" showText={false} />
        </Link>

        {/* Desktop Navigation */}
        <ul className="navbar-links">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
              >
                {link.label}
                {isActive(link.path) && (
                  <motion.div
                    className="nav-link-underline"
                    layoutId="underline"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            </li>
          ))}
          {!isAuthenticated ? (
            <li>
              <Link
                to="/employee"
                className="nav-link employee-link-desktop"
                style={{
                  background: 'linear-gradient(135deg, #C6A34F 0%, #D4B86A 100%)',
                  color: '#0B132B',
                  fontWeight: '700',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  boxShadow: '0 4px 15px rgba(198, 163, 79, 0.3)',
                  border: 'none'
                }}
              >
                Employee Login
              </Link>
            </li>
          ) : (
            <>
              {isAdmin && (
                <li>
                  <Link
                    to="/admin-visual"
                    className="nav-link admin-link-desktop"
                    style={{
                      background: 'linear-gradient(135deg, #4B7555 0%, #5a8a6a 100%)',
                      color: 'white',
                      fontWeight: '700',
                      padding: '0.75rem 2rem',
                      borderRadius: '8px',
                      boxShadow: '0 4px 15px rgba(75, 117, 85, 0.3)',
                      border: 'none'
                    }}
                  >
                    Edit Site
                  </Link>
                </li>
              )}
              <li>
                <a
                  href={process.env.REACT_APP_PHARMAPP_URL || 'https://pharmapp.web.app'}
                  className="nav-link employee-link-desktop"
                  style={{
                    background: 'linear-gradient(135deg, #C6A34F 0%, #D4B86A 100%)',
                    color: '#0B132B',
                    fontWeight: '700',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(198, 163, 79, 0.3)',
                    border: 'none'
                  }}
                >
                  Go to PharmApp
                </a>
              </li>
              <li>
                <button
                  onClick={handleLogout}
                  className="nav-link logout-link-desktop"
                  style={{
                    background: 'transparent',
                    color: '#0B132B',
                    fontWeight: '600',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(11, 19, 43, 0.3)',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </li>
            </>
          )}
        </ul>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={isMobileMenuOpen ? 'open' : ''}></span>
          <span className={isMobileMenuOpen ? 'open' : ''}></span>
          <span className={isMobileMenuOpen ? 'open' : ''}></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        initial={false}
        animate={{
          height: isMobileMenuOpen ? 'auto' : 0,
          opacity: isMobileMenuOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
      >
        <ul className="mobile-nav-links">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={isActive(link.path) ? 'active' : ''}
              >
                {link.label}
              </Link>
            </li>
          ))}
          {!isAuthenticated ? (
            <li>
              <Link
                to="/employee"
                onClick={() => setIsMobileMenuOpen(false)}
                className="employee-link"
              >
                Employee Login
              </Link>
            </li>
          ) : (
            <>
              {isAdmin && (
                <li>
                  <Link
                    to="/admin-visual"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="employee-link"
                  >
                    Edit Site Images
                  </Link>
                </li>
              )}
              <li>
                <a
                  href={process.env.REACT_APP_PHARMAPP_URL || 'https://pharmapp.web.app'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="employee-link"
                >
                  Go to PharmApp
                </a>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="logout-link-mobile"
                  style={{
                    background: 'transparent',
                    color: '#0B132B',
                    fontWeight: '600',
                    padding: '0.75rem 0',
                    border: 'none',
                    borderBottom: '1px solid rgba(198, 163, 79, 0.1)',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    fontSize: '1rem'
                  }}
                >
                  Logout
                </button>
              </li>
            </>
          )}
        </ul>
      </motion.div>
    </motion.nav>
  );
};

export default Navbar;

